import { CanonicalLoad, ClientLoadModel, EmailReplyPreference, LoadFindResultState } from '@numeo/types-core'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { filterByExternalIds, filterByMatchingCriteriasKeys } from 'utils/filterIngestingLoadUtils'
import { loadsMatch, mergeLoadData, nowDate } from 'utils/mergeLoadIngestUtils'

// RTS Rating data interface
export interface RTSRatingData {
    rating: string
    name: string
    mcNumber: string
    averageDaysToPayPast60?: string
    city?: string
    state?: string
    lastActivity?: string
    lastCreditScoreChange?: string
    phone?: string
    email?: string
    dotNumber?: string
    address?: string
    collectionQueue?: string
    previousRating?: string
}

export interface LoadSearchState {
    lastSearchResults: Record<string, LoadFindResultState | undefined>
    rtsRatings: Record<string, RTSRatingData>
    newLoadsCounts: Record<string, number>
    truckOrder: string[]
    emailReplyPreference?: EmailReplyPreference
    ingestedLoads: Record<string, CanonicalLoad[]>
    matchingCriteriaKeys: string[]
    loadExternalIds: string[]
}

// Load truck order from localStorage if available
const getSavedTruckOrder = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            return localStorage.getItem('truckOrder')
        } catch (error) {
            console.error('Failed to load truck order from localStorage:', error)
            return null
        }
    }
    return null
}
const savedTruckOrder = getSavedTruckOrder()

const initialState: LoadSearchState = {
    lastSearchResults: {},
    rtsRatings: {},
    newLoadsCounts: {},
    truckOrder: savedTruckOrder ? JSON.parse(savedTruckOrder) : [],
    ingestedLoads: {},
    matchingCriteriaKeys: [],
    loadExternalIds: [],
}

const loadSearchSlice = createSlice({
    name: 'loadSearch',
    initialState,
    reducers: {
        setAllLastSearchResults(state, action: PayloadAction<Record<string, LoadFindResultState>>) {
            Object.entries(action.payload).forEach(([truckId, newResult]) => {
                const lastSearchResult = state.lastSearchResults[truckId]

                if (!lastSearchResult) {
                    state.lastSearchResults[truckId] = newResult
                    return
                }
            })
        },
        setLastSearchResult(
            state,
            action: PayloadAction<{
                truckId: string
                newResult: LoadFindResultState
            }>
        ) {
            const { truckId, newResult } = action.payload
            const currentResult = state.lastSearchResults[truckId]

            if (!currentResult || currentResult.loadSearchId !== newResult.loadSearchId) {
                state.lastSearchResults[truckId] = newResult
            } else if (currentResult.brokers && currentResult.brokers.length > 0 && newResult.brokers?.length === 0) {
                state.lastSearchResults[truckId] = {
                    ...newResult,
                    brokers: currentResult.brokers,
                }
            } else {
                state.lastSearchResults[truckId] = newResult
            }
        },
        setLoadsForTruck(
            state,
            action: PayloadAction<{
                truckId: string
                loads: Record<string, ClientLoadModel>
                isNewLoad?: boolean
            }>
        ) {
            const { truckId, loads, isNewLoad } = action.payload
            if (state.lastSearchResults[truckId]) {
                Object.assign(state.lastSearchResults[truckId].loads, loads)

                if (isNewLoad) {
                    const urlParams = new URLSearchParams(window.location.search)
                    const currentTruckId = urlParams.get('truckId')

                    if (truckId !== currentTruckId) {
                        state.newLoadsCounts[truckId] = (state.newLoadsCounts[truckId] || 0) + Object.keys(loads).length
                    }
                }
            }
        },
        clearTruckLoads(state, action: PayloadAction<string>) {
            const truckId = action.payload
            if (state.lastSearchResults[truckId]) {
                const clearedResult = {
                    ...state.lastSearchResults[truckId],
                    loads: {},
                }
                state.lastSearchResults[truckId] = clearedResult
            }
        },
        updateLoadsInSearchResult(
            state,
            action: PayloadAction<{
                truckId: string
                updatedLoads: Record<string, ClientLoadModel>
            }>
        ) {
            const { truckId, updatedLoads } = action.payload
            const currentResult = state.lastSearchResults[truckId]

            if (currentResult) {
                const newLoads = { ...currentResult.loads }
                Object.entries(updatedLoads).forEach(([loadId, load]) => {
                    newLoads[loadId] = load
                })

                state.lastSearchResults[truckId] = {
                    ...currentResult,
                    loads: newLoads,
                }
            }
        },
        setRTSRating(
            state,
            action: PayloadAction<{
                mcNumber: string
                ratingData: RTSRatingData
            }>
        ) {
            const { mcNumber, ratingData } = action.payload
            state.rtsRatings[mcNumber] = ratingData
        },
        resetNewLoadsCount(state, action: PayloadAction<string>) {
            const truckId = action.payload
            state.newLoadsCounts[truckId] = 0
        },
        setTruckOrder(state, action: PayloadAction<string[]>) {
            state.truckOrder = action.payload
            try {
                localStorage.setItem('truckOrder', JSON.stringify(action.payload))
            } catch (error) {
                console.error('Failed to save truck order to localStorage:', error)
            }
        },
        changeEmailReplyPreference(state, action: PayloadAction<{ emailReplyPreference: EmailReplyPreference }>) {
            const { emailReplyPreference } = action.payload
            state.emailReplyPreference = emailReplyPreference
        },
        updateIngestedLoad(state, action: PayloadAction<CanonicalLoad>) {
            const incoming = action.payload
            const externalId = incoming.externalId
            const externalIds: string[] = state.loadExternalIds ?? []
            if (externalIds.length && !filterByExternalIds(externalIds, incoming)) {
                return
            }

            const matching = filterByMatchingCriteriasKeys(state.matchingCriteriaKeys, incoming)
            if (!matching) {
                return
            }

            const bucket = state.ingestedLoads[externalId] ?? (state.ingestedLoads[externalId] = [])
            const idx = bucket.findIndex((l) => loadsMatch(l, incoming))
            if (idx !== -1) {
                const merged = mergeLoadData(incoming, bucket[idx])
                merged.updatedAt = nowDate()
                bucket[idx] = merged
            } else {
                bucket.push({ ...incoming })
            }
        },
        updateIngestedLoads(state, action: PayloadAction<Record<string, CanonicalLoad[]>>) {
            for (const [externalId, incomingLoads] of Object.entries(action.payload)) {
                const bucket = state.ingestedLoads[externalId] ?? (state.ingestedLoads[externalId] = [])
                for (const incoming of incomingLoads) {
                    const idx = bucket.findIndex((l) => loadsMatch(l, incoming))
                    if (idx !== -1) {
                        const merged = mergeLoadData(incoming, bucket[idx])
                        merged.updatedAt = nowDate()
                        bucket[idx] = merged
                    } else {
                        bucket.push({ ...incoming })
                    }
                }
            }
        },
        setMatchingCriteriaKeys(state, action: PayloadAction<string[]>) {
            state.matchingCriteriaKeys = action.payload
        },
        setLoadExternalIds(state, action: PayloadAction<string[]>) {
            state.loadExternalIds = action.payload
        },
    },
})

export const {
    setAllLastSearchResults,
    setLastSearchResult,
    clearTruckLoads,
    updateLoadsInSearchResult,
    setRTSRating,
    setLoadsForTruck,
    resetNewLoadsCount,
    setTruckOrder,
    changeEmailReplyPreference,
    updateIngestedLoad,
    updateIngestedLoads,
    setMatchingCriteriaKeys,
    setLoadExternalIds,
} = loadSearchSlice.actions

export const selectLoadSearchState = (state: RootState) => state.loadSearch

export default loadSearchSlice.reducer
